import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { privacyPolicyPageFooter } from './layout/footerConfigs'

export const PrivacyPolicyPage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { isScrolled: _isScrolled, scrollProgress } = useOptimizedScroll()

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}

      <motion.div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transform: `translateY(${scrollProgress * -20}px)`
        }}
        transition={{ duration: 0 }}
      >
        {/* Randomly Moving Gradient Orbs */}
        <motion.div
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            top: '10%',
            left: '5%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 100, -50, 0],
            y: [0, -80, 60, 0],
            scale: [1, 1.2, 0.9, 1]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            top: '40%',
            right: '8%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -70, 90, 0],
            y: [0, 100, -60, 0],
            scale: [1, 0.8, 1.3, 1]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            bottom: '20%',
            left: '15%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -60, 80, 0],
            y: [0, -90, 40, 0],
            scale: [1, 1.1, 0.85, 1]
          }}
          transition={{
            duration: 28,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />
        <ContentSection prefersReducedMotion={prefersReducedMotion.current} />
      </motion.div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative pt-48 pb-32 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          Your Privacy, Our Priority
        </motion.h1>
        <motion.p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed mb-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}>
          At Mernaki, we respect your data and believe you should always know what information we collect and how we use it.
        </motion.p>
        <motion.p className="text-base mb-8" style={{ color: '#999999', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0.0, 0.2, 1] }}>
          We're fully compliant with the EU General Data Protection Regulation (GDPR)
        </motion.p>
        <motion.p className="text-sm" style={{ color: '#CCCCCC', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.8, delay: 0.7, ease: [0.4, 0.0, 0.2, 1] }}>
          Last updated: January 13, 2026
        </motion.p>
      </div>
    </section>
  )
}

const ContentSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  const sections = [
    {
      title: '1. What We Collect',
      content: 'We collect information to improve your experience. Depending on how you interact with Mernaki, we may collect:',
      items: [
        { label: 'Account Data', desc: 'Name, email, company name, language preference' },
        { label: 'Usage Data', desc: 'Pages visited, actions on site, browser/device type' },
        { label: 'Communication Data', desc: 'Emails or forms you send to us' },
        { label: 'Payment Data', desc: 'Billing name and details (handled securely via Stripe)' }
      ]
    },
    {
      title: '2. How We Use Your Data',
      content: 'We use your data to:',
      items: [
        { desc: 'Provide and improve our services' },
        { desc: 'Communicate with you about support or product updates' },
        { desc: 'Analyze usage to enhance the user experience' },
        { desc: 'Comply with legal obligations (e.g., tax or invoicing)' }
      ],
      highlight: 'We never sell or rent your personal data — ever.'
    },
    {
      title: '3. How We Store and Protect It',
      content: 'Your data security is our top priority:',
      items: [
        { desc: 'Your data is stored securely on servers located in the EU' },
        { desc: 'We use encryption (HTTPS, SSL) and restricted access' },
        { desc: 'Only authorized team members can access user data when necessary' }
      ]
    },
    {
      title: '4. Cookies & Tracking',
      content: 'Mernaki uses cookies and similar technologies to improve website functionality and measure engagement:',
      items: [
        { label: 'Essential Cookies', desc: 'Required for site operation' },
        { label: 'Analytics Cookies', desc: 'Help us understand usage patterns' },
        { label: 'Marketing Cookies', desc: 'Only if you have given consent' }
      ]
    },
    {
      title: '5. Sharing Information',
      content: 'We may share limited data with:',
      items: [
        { desc: 'Service providers (e.g., hosting, analytics, email)' },
        { desc: 'Legal authorities if required by law' }
      ],
      highlight: 'We do not share personal data with advertisers or unrelated third parties.'
    },
    {
      title: '6. Your Rights (GDPR)',
      content: 'Under the GDPR, you have the right to:',
      items: [
        { desc: 'Access, correct, or delete your data' },
        { desc: 'Withdraw consent at any time' },
        { desc: 'Request a copy of your data (data portability)' }
      ],
      highlight: 'To exercise these rights, email us at: privacy@mernaki.de'
    },
    {
      title: '7. Data Retention',
      content: 'We keep your data only as long as necessary to provide our services or comply with legal obligations. When no longer needed, it is securely deleted.'
    },
    {
      title: '8. Updates to This Policy',
      content: 'We may occasionally update this Privacy Policy to reflect changes in our practices or for legal reasons. The updated version will always be available here with a new "last updated" date.'
    },
    {
      title: '9. Contact Us',
      content: 'If you have any questions about this Privacy Policy or our data practices, please contact us at:',
      contact: {
        company: 'Mernaki GmbH',
        email: 'privacy@mernaki.de',
        address: 'Ludwigstraße 8, 85354 Freising, Bavaria, Germany'
      }
    }
  ]

  return (
    <section ref={ref} className="relative py-16" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {sections.map((section, index) => (
          <motion.div
            key={section.title}
            className="mb-16"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <h2 className="text-3xl md:text-4xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }}>
              {section.title}
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }}>
              {section.content}
            </p>

            {section.items && (
              <ul className="space-y-4 mb-6">
                {section.items.map((item: any, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span style={{ color: '#4A90E2', fontSize: '20px', marginTop: '2px' }}>•</span>
                    <div>
                      {item.label && <strong style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>{item.label}: </strong>}
                      <span style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {section.highlight && (
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(74, 144, 226, 0.1)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
                <p style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
                  {section.highlight}
                </p>
              </div>
            )}

            {section.contact && (
              <div className="space-y-2">
                <p style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>{section.contact.company}</p>
                <p style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
                  Email: <a href={`mailto:${section.contact.email}`} style={{ color: '#4A90E2', textDecoration: 'none' }}>{section.contact.email}</a>
                </p>
                <p style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
                  Address: {section.contact.address}
                </p>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  // const [heartHover, setHeartHover] = React.useState(false)

  if (isMobile) {
    return <PageMobileFooter {...privacyPolicyPageFooter} />
  }

  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mini Callout / Reassurance Line */}
        <div className="text-center py-8 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <motion.div
            className="flex items-center justify-center gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <p
              className="text-sm"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400
              }}
            >
              Have questions about your privacy?
            </p>
          </motion.div>
          <motion.p
            className="text-sm mt-1"
            style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 500
            }}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
          >
            Our team is here to help — <a href="mailto:privacy@mernaki.de" style={{ color: '#4A90E2', textDecoration: 'none' }}>contact us</a>.
          </motion.p>
        </div>

        {/* Main Footer Grid */}
        <div className="py-16">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki"
                  className="object-contain opacity-85"
                  style={{ height: '42px', width: 'auto', filter: 'brightness(0) invert(1)' }}
                />
                <h3 className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive", fontWeight: 400 }}>
                  Mernaki
                </h3>
              </div>
              <p
                className="text-sm mb-6"
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400,
                  lineHeight: '1.6'
                }}
              >
                Empowering businesses with modern recruitment tools.
              </p>
              <p
                className="text-xs"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                © {new Date().getFullYear()} Mernaki. All rights reserved.
              </p>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h4
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Company
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/about"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/partnerships"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Partnerships
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/team"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Team
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3 - Support */}
            <div>
              <h4
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Support
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/help"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/report-issue"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Report an Issue
                  </Link>
                </li>
                <li>
                  <Link
                    to="/status"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Status Page
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 4 - Legal */}
            <div>
              <h4
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Legal
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/privacy"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookies"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/impressum"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Impressum
                  </Link>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t py-6" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
              Built in Germany | Secure & GDPR Compliant
            </p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}
